import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Page header — title, optional subtitle, optional right-rail actions.
 *
 * Sits at the top of every route content area. Consistent rhythm
 * across routes is one of the loudest "this is a real product" signals.
 */
export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Optional eyebrow text — small uppercase label above the title
   * (e.g. project slug on the project-detail page). */
  eyebrow?: string;
  /** Right-rail actions — buttons, dropdowns, search inputs. */
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-b border-border-subtle pb-4",
        "sm:flex-row sm:items-end sm:justify-between sm:gap-6",
        className
      )}
    >
      <div className="flex flex-col gap-1">
        {eyebrow && <span className="label-micro text-muted-foreground">{eyebrow}</span>}
        <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
